import { Component } from 'react';
import { connect } from 'react-redux';
import { withTheme } from 'styled-components';
import {
  path,
  contains,
  without,
  compose,
  map,
  toPairs,
  pathEq,
  isEmpty,
} from 'ramda';
import { number, string, func, objectOf, arrayOf, bool } from 'prop-types';
import { getSingleValueData } from '../../selectors/crimes';
import { selectLocations } from '../../actions/options';
import { getCityNameById } from '../../selectors/locations';
import { getSelectedLocationsIds } from '../../selectors/options';
import { getColorScale, getExtent } from '../../utils/helpers';
import { getCensusCrimesPending } from '../../reducers/crimes';
import { isCensusView } from '../../selectors/router';

const censusLayer = process.env.REACT_APP_MAPBOX_CENSUS_LAYER;

const FILL_COLOR = 'fill-color';
const FILL_OPACITY = 'fill-opacity';
const OUTLINE_COLOR = 'fill-outline-color';
const WHITE = 'rgba(255, 255, 255, 1)';
const TRANSPARENT = 'rgba(255, 255, 255, 0)';

export class CensusMap extends Component {
  map = null;

  componentWillReceiveProps(nextProps) {
    const { data, hoveredId, selectedIds, censusView } = nextProps;
    const {
      data: prevData,
      hoveredId: prevHoveredId,
      selectedIds: prevSelectedIds,
    } = this.props;

    if (!data) return;
    if (!censusView) return;

    if (prevData !== data && Object.keys(data).length !== 0) {
      this.renderHeatmap(data);
      this.renderHighlights(selectedIds, hoveredId);
    }
    if (Object.keys(data).length === 0 && prevData !== data)
      this.clearHeatMap(prevData);

    if (hoveredId !== prevHoveredId || selectedIds !== prevSelectedIds) {
      this.renderHighlights(selectedIds, hoveredId);
    }
  }

  init = el => {
    if (!el) return null;

    this.map = el.getMap();
    this.map.on('style.load', () => {
      const waiting = () => {
        if (this.map.isStyleLoaded()) {
          setTimeout(waiting, 200);
        } else {
          this.mapLoaded();
        }
      };
      waiting();
    });
  };

  mapLoaded = () => {
    const { data, hoveredId, selectedIds } = this.props;
    this.renderHeatmap(data);
    this.renderHighlights(selectedIds, hoveredId);
  };

  onHover = ({ features, ...rest }) => {
    if (!this.map.isStyleLoaded()) return;
    const census = features.find(pathEq(['layer', 'id'], censusLayer));
    const id = path(['properties', 'GEOID'], census);
    const {
      data,
      hoveredId,
      cityName,
      onMouseLeave,
      cityId,
      theme,
    } = this.props;

    if (id === hoveredId) return;

    if (!census) {
      // Not so obvious way of checking if we are in census mode
      if (cityId) onMouseLeave();
      return;
    }

    if (typeof data[id] === 'undefined') {
      return;
    }

    const { properties } = census;
    const [min, max] = getExtent(data);
    const colorScale = getColorScale(min, max);

    const { x, y } = this.map.project([
      parseFloat(properties.INTPTLON),
      parseFloat(properties.INTPTLAT),
    ]);

    // Data passed into tooltip
    this.props.onMouseEnter({
      id,
      mainValue: data[id],
      sideColor:
        typeof data[id] === 'number'
          ? colorScale(data[id])
          : theme.colors.grey300,
      x,
      y,
      title: id, // Also there is properties.NAMELSAD
      subtitle: cityName,
      isPartial: false, // TODO
    });
  };

  onClick = e => {
    if (!this.map.isStyleLoaded()) return;
    const census = e.features.find(pathEq(['layer', 'id'], censusLayer));
    const id = path(['properties', 'GEOID'], census);
    const { select, selectedIds, data } = this.props;

    if (!census || !data[id]) return;

    const allIds = Object.keys(data);

    // Treat all selected like none selected
    if (selectedIds.length === allIds.length) {
      select([id]);
    } else {
      let withoutId = without([id], selectedIds);

      // If deselecting last location, select all
      if (isEmpty(withoutId)) {
        select(allIds);
        //Otherwise add or remove location from selections
      } else {
        select(contains(id, selectedIds) ? withoutId : [...selectedIds, id]);
      }
    }
  };

  setPolygonPaint = (styleProperty, defaultValue, specificValues) => {
    if (!specificValues.length) return;

    this.map.setPaintProperty(censusLayer, styleProperty, {
      property: 'GEOID',
      type: 'categorical',
      default: defaultValue,
      stops: specificValues,
    });
  };

  clearHeatMap = data => {
    const heatmapData = compose(
      map(([id, val]) => [id, 'rgba(0, 0, 0, 0)']),
      toPairs,
    )(data);
    this.setPolygonPaint(FILL_COLOR, 'rgba(0, 0, 0, 0)', heatmapData);
  };

  // Turns polygon map into a heatmap
  renderHeatmap = data => {
    const [min, max] = getExtent(data);
    const colorScale = getColorScale(min, max);

    // Array of shape [[id0, color0], [id1, color1], ...]
    const heatmapData = compose(
      map(([id, val]) => [id, colorScale(val, id)]),
      toPairs,
    )(data);
    this.setPolygonPaint(FILL_COLOR, 'rgba(0, 0, 0, 0)', heatmapData);
  };

  renderHighlights = (selectedIds, hoveredId) => {
    this.setPolygonPaint(
      OUTLINE_COLOR,
      TRANSPARENT,
      selectedIds.map(x => [x, WHITE]),
    );

    this.setPolygonPaint(FILL_OPACITY, 0.4, selectedIds.map(x => [x, 1]));
  };

  render() {
    return this.props.children({
      getMapRef: this.init,
      onHover: this.onHover,
      onClick: this.onClick,
      isLoading: this.props.isLoading,
    });
  }
}

CensusMap.propTypes = {
  cityId: string,
  cityName: string,
  selectedIds: arrayOf(string).isRequired,
  data: objectOf(number).isRequired,
  children: func.isRequired,
  onMouseEnter: func,
  onMouseLeave: func,
  onClick: func,
  select: func.isRequired,
  isLoading: bool,
};

export default connect(
  (state, ownProps) => ({
    data: getSingleValueData(state),
    cityName: getCityNameById(state, ownProps.cityId),
    selectedIds: getSelectedLocationsIds(state),
    isLoading: getCensusCrimesPending(state),
    censusView: isCensusView(state),
  }),
  { select: selectLocations },
)(withTheme(CensusMap));



// WEBPACK FOOTER //
// ./src/components/MapPage/CensusMap.jsx